import {
  fitWithin,
  MAX_PHOTO_FULL_BYTES,
  MAX_PHOTO_THUMB_BYTES,
  PHOTO_FULL_MAX_PX,
  PHOTO_THUMB_MAX_PX,
  type PhotoMime,
} from '@reditelji/shared';

/**
 * Turning a camera photo into the two derivatives the API stores.
 *
 * The work happens here, in the browser, rather than on the server: a
 * coordinator standing at the post uploads from their phone, and sending a
 * 12 MP original over a mobile uplink to have it thrown away after resizing is
 * the slowest possible way to do this. It also keeps the API free of a native
 * image dependency. Re-encoding drops every EXIF field on the way, GPS
 * included — the orientation flag is applied first, so nothing lands sideways.
 */

/** Anything the browser hands us above this is a mistake, not a photo. */
const MAX_SOURCE_BYTES = 40 * 1024 * 1024;

/** Encoder quality steps, tried in order until the blob fits its cap. */
const QUALITY_STEPS = [0.82, 0.72, 0.62, 0.5];

export interface PreparedPhoto {
  /** Base64, ready for the JSON body. */
  full: string;
  thumb: string;
  mime: PhotoMime;
  width: number;
  height: number;
}

/** An error with a sentence a coordinator can act on. */
export class PhotoError extends Error {}

export async function preparePhoto(file: File): Promise<PreparedPhoto> {
  if (file.type && !file.type.startsWith('image/')) {
    throw new PhotoError(`»${file.name}« ni slika.`);
  }
  if (file.size > MAX_SOURCE_BYTES) {
    throw new PhotoError(`»${file.name}« je prevelika (${Math.round(file.size / 1024 / 1024)} MB).`);
  }

  // Decoded once. A 12 MP photo is the expensive part of this whole flow, and
  // both derivatives can be resized off the one bitmap — `createImageBitmap`
  // takes a bitmap as a source, so neither costs a second decode of the file.
  const source = await decodeFile(file);
  const fullSize = fitWithin(source.width, source.height, PHOTO_FULL_MAX_PX);
  const thumbSize = fitWithin(source.width, source.height, PHOTO_THUMB_MAX_PX);

  try {
    const full = await encode(file, source, fullSize, MAX_PHOTO_FULL_BYTES);
    // The thumb is resized from the original rather than from the full
    // derivative: one resize is sharper than two, and the strip is the version
    // a marshal actually looks at.
    const thumb = await encode(file, source, thumbSize, MAX_PHOTO_THUMB_BYTES, full.mime);

    return {
      full: await toBase64(full.blob),
      thumb: await toBase64(thumb.blob),
      mime: full.mime,
      width: fullSize.width,
      height: fullSize.height,
    };
  } finally {
    source.close();
  }
}

/** Decode the file at its own size, with the EXIF orientation applied. */
async function decodeFile(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // Chrome and Firefox cannot decode HEIC, which is what an iPhone shoots by
    // default; Safari can, and iOS converts on upload, so this mostly bites a
    // file dragged onto a desktop.
    throw new PhotoError(
      `»${file.name}« se ne da odpreti v tem brskalniku. Če je HEIC, jo shrani kot JPEG in poskusi znova.`,
    );
  }
}

/**
 * Scale a decoded bitmap, letting the browser filter: `resizeQuality: 'high'`
 * does it properly, where a single `drawImage` step from 4000 px to 640 px
 * aliases.
 */
async function resize(
  source: ImageBitmap,
  size: { width: number; height: number },
): Promise<ImageBitmap> {
  return createImageBitmap(source, {
    resizeWidth: size.width,
    resizeHeight: size.height,
    resizeQuality: 'high',
  });
}

async function encode(
  file: File,
  source: ImageBitmap,
  size: { width: number; height: number },
  maxBytes: number,
  force?: PhotoMime,
): Promise<{ blob: Blob; mime: PhotoMime }> {
  const bitmap = await resize(source, size);
  const canvas = makeCanvas(size.width, size.height);
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null;
  if (!ctx) throw new PhotoError('Brskalnik ne zna pripraviti slike za nalaganje.');
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  // WebP everywhere it exists; a browser that silently hands back something
  // else gets taken at its word, since the API accepts JPEG and PNG too.
  for (const type of force ? [force] : (['image/webp', 'image/jpeg'] as const)) {
    for (const quality of QUALITY_STEPS) {
      const blob = await toBlob(canvas, type, quality);
      // An encoder that hands back another format has refused this one. When a
      // format was forced, both derivatives have to agree on it — the upload
      // declares one mime for the pair — so there is nothing left to try.
      if (blob.type !== type) {
        if (force) {
          throw new PhotoError(
            `»${file.name}« se v tem brskalniku ne da shraniti v enotnem zapisu. Shrani jo kot JPEG in poskusi znova.`,
          );
        }
        break;
      }
      if (blob.size <= maxBytes) return { blob, mime: type };
    }
  }
  throw new PhotoError(
    `»${file.name}« se ne da stisniti dovolj. Poskusi z manjšo sliko ali jo prej obreži.`,
  );
}

function makeCanvas(width: number, height: number): OffscreenCanvas | HTMLCanvasElement {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function toBlob(
  canvas: OffscreenCanvas | HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob> {
  if (canvas instanceof HTMLCanvasElement) {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new PhotoError('Slike ni bilo mogoče kodirati.'))),
        type,
        quality,
      );
    });
  }
  return canvas.convertToBlob({ type, quality });
}

/**
 * Base64 in fixed chunks. `String.fromCharCode(...bytes)` over a 900 kB buffer
 * spreads a million arguments onto the stack and throws.
 */
async function toBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}
